import React, { useState, useEffect } from "react";
import { API_BASE } from "../config";

export function ArchivesAdmin({ user }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [toast, setToast]       = useState({ type: "", msg: "" });

  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const limit = 20;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${user.token}`,
  };

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  const fetchArchives = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("page",  String(page));
      params.set("limit", String(limit));
      if (typeFilter) params.set("type", typeFilter);

      const res  = await fetch(`${API_BASE}/admin/archives?${params}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Erreur chargement");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArchives(); }, [page, typeFilter]); // eslint-disable-line

  const pages = Math.max(1, Math.ceil(total / limit));

  const doRestore = async () => {
    if (!restoreConfirm) return;
    setRestoreLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/archives/${restoreConfirm.id}/restaurer`, {
        method: "POST",
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur restauration");

      setItems((prev) =>
        prev.map((a) =>
          a.id === restoreConfirm.id
            ? { ...a, restored_at: new Date().toISOString() }
            : a
        )
      );
      showToast("success", `Compte restauré${data.rdv_note ? " — " + data.rdv_note : ""}`);
    } catch (e) {
      showToast("error", e.message);
    } finally {
      setRestoreLoading(false);
      setRestoreConfirm(null);
    }
  };

  const typeBadge = (t) => {
    const map = { USAGER: "usagerBadge", MEDECIN: "medecinBadge", GESTIONNAIRE: "gestionnaireBadge" };
    return <span className={`roleBadge ${map[t] || ""}`}>{t}</span>;
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

  return (
    <div className="adminPanel">

      <div className="adminPanelHeader">
        <div />
        <button
          className="btnSecondary"
          onClick={async () => { setPage(1); await fetchArchives(); showToast("success", "Liste mise à jour"); }}
          disabled={loading}
        >
          {loading ? "Chargement..." : "Rafraîchir"}
        </button>
      </div>

      <div className="adminToolbar">
        <select
          className="adminSelect"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
        >
          <option value="">Tous les types</option>
          <option value="USAGER">Usager</option>
          <option value="MEDECIN">Médecin</option>
          <option value="GESTIONNAIRE">Collectivité</option>
        </select>
      </div>

      {error && <div className="adminError">{error}</div>}
      {toast.msg && <div className={`adminToast ${toast.type}`}>{toast.msg}</div>}

      <div className="adminTableWrap">
        <table className="adminTable">
          <thead>
            <tr>
              <th>Email archivé</th>
              <th>Type</th>
              <th>Archivé le</th>
              <th>Restauré le</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="adminEmpty">Aucune archive.</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id}>
                <td className="adminMono">{a.original_email || "—"}</td>
                <td>{typeBadge(a.type)}</td>
                <td style={{ fontSize: 12 }}>{fmtDate(a.archived_at)}</td>
                <td style={{ fontSize: 12 }}>
                  {a.restored_at
                    ? <span style={{ color: "#16a34a" }}>{fmtDate(a.restored_at)}</span>
                    : <span style={{ color: "#9ca3af" }}>—</span>}
                </td>
                <td>
                  {!a.restored_at && (
                    <button
                      className="btnSecondaryOutline"
                      onClick={() => setRestoreConfirm({ id: a.id, email: a.original_email, type: a.type })}
                    >
                      Restaurer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="adminPager">
        <button className="btnSecondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>←</button>
        <div className="adminPagerInfo">Page <b>{page}</b> / {pages} — Total: <b>{total}</b></div>
        <button className="btnSecondary" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>→</button>
      </div>

      {restoreConfirm && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <div className="logoutModalTitle">Restaurer le compte</div>
            <div className="logoutModalText">
              Le compte <strong>{restoreConfirm.email}</strong> ({restoreConfirm.type}) sera
              réinséré dans les tables actives.<br />
              Les rendez-vous ne seront pas restaurés (créneaux potentiellement pris).
            </div>
            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setRestoreConfirm(null)} disabled={restoreLoading}>
                Annuler
              </button>
              <button className="btnPrimary" onClick={doRestore} disabled={restoreLoading}>
                {restoreLoading ? "Restauration..." : "Confirmer la restauration"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
